/**
 * The power-up seam: an interface and a registry, not a pile of abilities.
 *
 * An orb is a *lookup*, never a branch. `PowerUpState.kind` is a registry id and every side of
 * the game — the simulation deciding what an orb is, the wire carrying it, the HUD drawing what
 * you are holding, the phase that fires it — resolves it through `getPowerUp`. Adding the next
 * power-up is therefore a file in this directory and one entry in `POWER_UPS`, exactly as a
 * second mini-game is (`../minigames`), and no `if` anywhere changes.
 *
 * This lives in `@chompdome/shared` rather than in `@chompdome/game-core` because three
 * processes need it and they need different halves: the simulation reads the id to stamp on an
 * orb and the spec to fire one, the gateway puts both on the wire, and the browser reads `glyph`
 * and `name` to draw the inventory slot.
 *
 * WHAT AN ABILITY IS, AND WHAT IT IS NOT. A `PowerUp` is description plus a *spec* — `data`, not
 * code. It says how far a thing flies, how long it lives, when it arms and what it does on
 * contact; it never touches the world, because the world is `@chompdome/game-core`'s and has to
 * stay driven by `(state, inputs, tick)` to replay. `RoomSim.stepProjectiles` reads the spec and
 * does the arithmetic, and it branches on `effect` — a *class* of effect that any number of
 * power-ups may share — never on an id. That is the line: a fourth power-up that shatters what it
 * hits is a new file here and nothing else; a fifth that does something no `ProjectileEffect`
 * describes is a new effect, and the one place it is spelled out is `step`.
 *
 * **NOT EVERY ABILITY PUTS SOMETHING IN THE WORLD, and `ProjectileSpec` is therefore OPTIONAL.**
 * This document's own design notes said the pressure would come from a shield — "a `PowerUp`
 * whose spec is absent and whose effect is a tick count on the player… exactly the kind of
 * pressure the registry should be put under before it is trusted" — and the teleport is that
 * case: firing it spawns nothing, collides with nobody and is over on the tick it is pressed.
 * So an entry carries **exactly one** of two specs, and which one it is decides which of the two
 * things firing does:
 *
 * - `projectile` — a body in the world, carried by `stepProjectiles` until one of its bounds
 *   retires it. Seven of the nine entries.
 * - `self` — something done to the player who fired it, there and then, in the phase that sits
 *   beside `stepSplits` and `stepEjects`. Two of the nine — the teleport and the invisibility.
 *   See `SelfSpec`.
 *
 * The pair is what the simulation reads the registry through (`projectileSpecOf` / `selfSpecOf`)
 * and it still never names a power-up: an absent `projectile` is answered as "no projectile"
 * rather than as an error, which is the same answer an id this build does not register gets, and
 * the fallthrough is the same either way — the slot empties and nothing happens.
 */

/**
 * How a projectile gets where it is going.
 *
 * `launched` leaves along the firer's heading at `speed` and stops at `range` — a rocket, a
 * grapple head. `dropped` is placed *behind* the firer and never moves; what moves is whoever
 * drives into it, which is the whole of a mine.
 */
export type ProjectileMotion = 'launched' | 'dropped';

/**
 * What contact does. A class of outcome rather than a power-up's name, so two power-ups that
 * break a cell apart share one implementation and one set of tests.
 *
 * `shatter` — break the cell it touched over the existing spike machinery (`RoomSim.shatterCell`).
 * `tether`  — hook the player it touched and haul the *lighter* of the two ends toward the
 *             heavier for a bounded window. See `TetherProjectileSpec` for why that is the whole
 *             of "if the circle is bigger, they eat you".
 * `freeze`  — leave a timed status on the player it touched: for `freezeTicks` they cannot move
 *             and cannot act, and — unlike a duellist — they can still be eaten. It is the one
 *             effect here whose consequence outlives the tick it landed on, which is why it is a
 *             new effect rather than a re-tuning of an existing one.
 * `slow`    — leave a timed status that takes *half* of somebody's speed rather than all of it,
 *             and nothing else at all. The second effect whose consequence outlives its own body,
 *             and it is a distinct one rather than a gentle `freeze` because a freeze is a
 *             statement about what a player may *do* and this is only a number their steering is
 *             multiplied by: everything a slowed player could do a tick ago, they can still do.
 * `duel`    — pull the player it touched into a mini-game duel with whoever fired it, whatever
 *             the two of them weigh. The third effect that outlives its own body and by far the
 *             longest-lived: what is left behind is a `DuelState`, which freezes both of them for
 *             DUEL_DECIDE_TICKS and then moves mass. It is a new effect rather than a setting on
 *             any of the four above it because none of them describes it — a duel is not a thing
 *             done *to* somebody, it is a thing the two of them are now in.
 */
export type ProjectileEffect = 'shatter' | 'tether' | 'freeze' | 'slow' | 'duel';

interface ProjectileCommon {
  readonly motion: ProjectileMotion;
  /** World units per second while flying; 0 for a dropped body. */
  readonly speed: number;
  /**
   * The hard ceiling on how far this thing may travel, in world units, and the reason none of
   * these is a map-wide grief: a projectile is retired the tick its travel reaches this, whatever
   * else is going on. 0 for a dropped body, which never travels at all.
   */
  readonly range: number;
  /** Ticks it lives if it hits nothing. The second bound, and the only one a mine has. */
  readonly lifetimeTicks: number;
  /** Ticks before it may hit anything at all — 0 for everything that is not a mine. */
  readonly armingTicks: number;
  /** Collision radius. A fixture's size, like SPIKE_RADIUS: shared, never on the wire. */
  readonly radius: number;
  /**
   * How far outside the firing cell's rim the thing appears, in world units — forwards for a
   * launched body, backwards for a dropped one.
   *
   * Here rather than in the simulation because it is the difference between a muzzle and a
   * trail: a rocket wants the smallest clearance that keeps it out of its own firer, and a mine
   * wants to land where a chaser is about to be.
   */
  readonly standoff: number;
  /**
   * Whether the player who fired it is a legal target once it is armed.
   *
   * True for exactly one thing, deliberately: a mine you have walked back onto is a mine that
   * goes off. See `mine.ts`.
   */
  readonly hitsOwner: boolean;
}

/** Break the cell it touched, over the spike shatter. */
export interface ShatterProjectileSpec extends ProjectileCommon {
  readonly effect: 'shatter';
}

/**
 * Hook a player and haul along the chain.
 *
 * **The lighter end moves.** That is the owner's rule implemented literally rather than as a
 * special case: "a chain that shoots out and grabs a circle and pulls them into you — if the
 * circle is bigger though, they would eat you". A grapple into something heavier reels *you* in,
 * and what happens when you arrive is the ordinary eat rule, decided by `stepPlayerCollisions`
 * like every other contact in the game. There is no "you lose" branch anywhere, and there must
 * not be one: the reversal has to be the same rule as the win.
 */
export interface TetherProjectileSpec extends ProjectileCommon {
  readonly effect: 'tether';
  /** Ticks the haul runs for once the chain connects. The window it is bounded by. */
  readonly haulTicks: number;
  /** World units per second the hauled end is dragged at, on top of its own steering. */
  readonly haulSpeed: number;
  /** Past this much distance between the two ends, the chain snaps. The other way out. */
  readonly breakDistance: number;
}

/**
 * Freeze the player it touched for a bounded number of ticks.
 *
 * **The status is on the player, not on this projectile**, which is what makes this the first
 * effect in the registry that outlives its own body: the shard is retired on the tick it lands,
 * exactly as a rocket is, and what is left behind is `PlayerState.frozenUntilTick` — a deadline
 * every phase of `step` reads and `world-hash.ts` hashes. See `freeze.ts` for what a frozen
 * player keeps, and why "still edible" is the whole point of the ability.
 */
export interface FreezeProjectileSpec extends ProjectileCommon {
  readonly effect: 'freeze';
  /** How long the player it hits is frozen for. The only bound on the status it leaves. */
  readonly freezeTicks: number;
}

/**
 * Slow the player it touched for a bounded number of ticks, and do nothing else to them.
 *
 * **The status is on the player, exactly as the freeze's is** — `PlayerState.slowedUntilTick`, a
 * deadline `stepMovement` reads and `world-hash.ts` hashes — which is what makes this the second
 * effect that outlives its own body: the patch is retired on the tick somebody crosses it, and
 * what is left is ten seconds of half speed on whoever that was.
 *
 * **HOW MUCH SPEED IS NOT ON THIS SPEC, and that is not an omission.** SLOW_SPEED_MULTIPLIER is a
 * shared constant because the multiplier is a property of the *status*, and the status outlives
 * the projectile that left it: by the tick a slowed player is steering there is no spec to read
 * it off. It is the freeze's arrangement — `freezeTicks` is here because it bounds the deadline,
 * and "a frozen player does not move" is not, because that is what the status means.
 *
 * See `frost.ts` for why one press lays one patch rather than a held streak, why the patch is
 * spent by the first player across it, and why it is visible to everybody.
 */
export interface SlowProjectileSpec extends ProjectileCommon {
  readonly effect: 'slow';
  /** How long the player it catches is slowed for. The only bound on the status it leaves. */
  readonly slowTicks: number;
}

/**
 * Pull the player it touched into a duel with the player who fired it.
 *
 * **THERE IS NOTHING ON IT, AND THAT IS THE HONEST SHAPE RATHER THAN AN OMISSION.** The two
 * numbers a duel gun is actually about — DUEL_GUN_STAKE_MULTIPLIER and DUEL_GUN_IMMUNITY_TICKS
 * — are shared constants for exactly SLOW_SPEED_MULTIPLIER's reason, one order of magnitude
 * further out: the projectile is retired on the tick it lands and what it leaves behind lives
 * for DUEL_DECIDE_TICKS, so by the time the stake is paid and the window is granted there is no
 * spec left to read either off. `FreezeProjectileSpec.freezeTicks` is here by the opposite test —
 * it *bounds* the deadline the shard leaves, which is a fact about the projectile.
 *
 * So this is `ShatterProjectileSpec`'s shape: a class of outcome and the ordinary flight fields.
 * A second ability that started duels would be a file in this directory and nothing else.
 *
 * See `duel-gun.ts` for why it flies exactly as a rocket does, why it may be fired at anybody at
 * all, and why the fact that it freezes its own firer for ten seconds is most of its price.
 */
export interface DuelProjectileSpec extends ProjectileCommon {
  readonly effect: 'duel';
}

export type ProjectileSpec =
  | ShatterProjectileSpec
  | TetherProjectileSpec
  | FreezeProjectileSpec
  | SlowProjectileSpec
  | DuelProjectileSpec;

/**
 * What firing a power-up does to the player who fired it, when it puts nothing in the world.
 *
 * The `ProjectileEffect` of the other half of the registry: a *class* of outcome the simulation
 * branches on, never a power-up's name. There are two.
 *
 * `teleport`  — move the firer's whole cluster to a point chosen the way a spawn point is, and
 *               give them a short window in which they cannot be touched.
 * `invisible` — take the firer out of the world for a while: nothing may reach them, and no other
 *               viewer is sent them at all.
 */
export type SelfEffect = 'teleport' | 'invisible';

/**
 * Jump the firer's cluster somewhere else on the map.
 *
 * **NOTHING HERE IS A POSITION, and that is the whole of why this is data.** Where a teleport
 * lands is drawn from the room's own seeded stream by the simulation, against the room's own
 * walls and the room's own spikes and players — a number written down here could not be any of
 * those things. What the spec carries is the *policy*: how much room the arrival wants, how hard
 * to look for it, and how long the grace on landing runs.
 *
 * See `teleport.ts` for why each of the three is the number it is, and for the argument that
 * arriving with no grace at all would make the ability worse than not pressing the button.
 */
export interface TeleportSelfSpec {
  readonly effect: 'teleport';
  /**
   * Ticks of the post-duel grace this grants on arrival — `PlayerState.duelImmuneUntil`, the
   * window that already means "cannot be eaten and cannot be pulled into a duel".
   */
  readonly graceTicks: number;
  /**
   * World units of empty space wanted around the arrival, on top of the cluster's own extent.
   * Both a spike and anybody big enough to eat one of its cells count against it.
   */
  readonly landingClearance: number;
  /**
   * How many points to draw before taking one anyway.
   *
   * A budget rather than a guarantee, exactly as SPIKE_PLACEMENT_ATTEMPTS is: a search that
   * looped until the world cooperated would be a tick with no bound on it, and a room where
   * every clear point has been taken is a room where landing badly is the honest answer.
   */
  readonly placementAttempts: number;
}

/**
 * Hide the firer from the world, and from every other viewer, for a while.
 *
 * **ONE NUMBER, AND EVERYTHING ELSE ABOUT IT IS A RULE RATHER THAN A VALUE.** What a ghost may
 * and may not do is not a set of knobs an entry could get wrong — it is the same list in every
 * phase of `step` that could touch a player, and it is written there once. So the spec carries
 * the only thing a second invisibility power-up could sensibly differ in, which is how long it
 * lasts.
 *
 * See `invisibility.ts` for the whole design: why the status and the slot's window are two
 * different thirty seconds, why a second press while it is running does nothing, and why "not
 * replicated" rather than "drawn faintly" is the only honest reading of the word.
 */
export interface InvisibleSelfSpec {
  readonly effect: 'invisible';
  /**
   * Ticks the firer is hidden for, counted from the press.
   *
   * A duration rather than a deadline, exactly as `FreezeProjectileSpec.freezeTicks` is: the
   * spec is data and the tick it is added to is the room's.
   */
  readonly invisibleTicks: number;
}

/** Everything an ability can do to its own firer. */
export type SelfSpec = TeleportSelfSpec | InvisibleSelfSpec;

/**
 * HOW MANY TIMES AN ORB IS WORTH, and it is declared here as *data* for exactly the reason a
 * `ProjectileSpec` is: the simulation branches on the **model** — a class of use any number of
 * power-ups may share — and never on a power-up's id.
 *
 * The player report is the whole of the design: "the missles should be unlimited for 10 seconds
 * (shoot as much as you can). The grappling hook can be used for 30 seconds. The freeze ray
 * should maybe be unlimited but the frost should maybe be 5 uses... but I like the rockets timed
 * as it's kind of a chaos inducing one which would be fun since it's timed and you're just firing
 * as much as you can until finished." So there are two models and there are deliberately only
 * two:
 *
 * - `timed` — a **window**. It runs from the tick the orb is taken and it is burning whether or
 *   not the button is ever pressed, which is what makes it chaos: the only way to get value out
 *   of one is to spend it, now, at whatever is in front of you.
 * - `charged` — a **budget**. It waits, costs nothing to hold, and is worth exactly as much on
 *   the tick it expires as on the tick it was picked up, because it does not expire.
 *
 * That difference is the whole of the switching rule (`RoomSim.stepPowerUpSwitch`): a window
 * somebody switches away from is *over* — the player asked for precisely that — and a budget
 * they switch away from keeps every charge it had. Neither of those is a special case about a
 * rocket or a frost patch; both are one branch on `model`.
 *
 * **`cooldownTicks` IS ON BOTH MODELS AND IT IS THE REAL RATE LIMIT.** "Unlimited for ten
 * seconds" cannot mean twenty rockets a second: MAX_INPUTS_PER_TICK bounds a client to one input
 * frame a tick, which is twenty firings a second, and MAX_PROJECTILES_PER_ROOM is a *room* cap
 * that would be reached by one player holding the button. So every entry declares the gap
 * between two of its own firings, and each one is derived from something the ability already
 * says about itself rather than picked out of the air — see each file for which number it is and
 * why. A charged entry needs one too, because five charges pressed on five consecutive ticks is
 * a quarter of a second of the strongest effect in the game.
 */
export type UseModelKind = 'timed' | 'charged';

interface UseModelCommon {
  /**
   * The fewest ticks between two firings of this power-up. The rate limit, and the answer to
   * "unlimited" — see the note above for why MAX_PROJECTILES_PER_ROOM is not one.
   */
  readonly cooldownTicks: number;
}

/** A window: fire as often as `cooldownTicks` allows until it runs out. */
export interface TimedUseModel extends UseModelCommon {
  /**
   * Ticks the window runs for, **counted from the first firing rather than from the pickup**.
   *
   * THIS REVERSES WHAT THIS INTERFACE ORIGINALLY SAID, AND THE PLAYER IS WHY: "the 10 seconds
   * rockets start as soon as it's collected. But the user may have other power ups currently in
   * play. The rockets should NOT start until the user fires the first rocket - then the 10
   * seconds should start." A window that burned from the pickup was coherent when a player
   * carried one power-up; it stopped being coherent the day `POWER_UP_INVENTORY_SIZE` became
   * three, because then the cost of using anything else was silently paid out of the window of
   * everything else you were holding.
   *
   * So a fresh timed slot is **not running**: `PowerUpSlot.expiresAtTick` is 0, the first firing
   * sets it to `tick + durationTicks`, and it is a **deadline** from that moment on — expiry
   * computed and never swept, the rule a ban's expiry, a freeze's thaw and a duel's grace are all
   * already under. What did not change is what the window *is*: once it starts nothing pauses it,
   * and holding the button is still the only way to be paid for one.
   */
  readonly durationTicks: number;
  readonly model: 'timed';
}

/** A budget: a fixed number of firings, and it never expires on its own. */
export interface ChargedUseModel extends UseModelCommon {
  readonly model: 'charged';
  /** Firings one orb is worth. Also what a top-up refills a held slot to. */
  readonly charges: number;
}

export type UseModel = TimedUseModel | ChargedUseModel;

/**
 * One kind of power-up. `id` is the wire value; the spec is what firing it does.
 *
 * **Exactly one of `projectile` and `self` is set** — see the note at the top of this file for
 * why the first of them is optional at all. `powerups.test.ts` asserts that over the whole
 * registry, so an entry that set both or neither fails there rather than in a room.
 */
export interface PowerUp {
  /** Stable across versions — it is on the wire and clients look their UI up by it. */
  readonly id: string;
  readonly name: string;
  /** One sentence, shown on the inventory slot. It is a tooltip, not a manual. */
  readonly description: string;
  /** A single glyph, so a phone-sized slot can be read at a glance. */
  readonly glyph: string;
  /**
   * How many times, or for how long, one orb of this is worth — see `UseModel`.
   *
   * Required on every entry, unlike the two specs below it: an ability with no use model is one
   * the simulation could not spend, and `powerups.test.ts` asserts the whole registry declares
   * exactly one model and states every bound on it.
   */
  readonly use: UseModel;
  /** What this puts in the world when it is spent, if it puts anything there at all. */
  readonly projectile?: ProjectileSpec;
  /** What this does to the player who spent it, if it acts on them instead. */
  readonly self?: SelfSpec;
}

/**
 * Ceiling on a power-up id, enforced by the protocol schema.
 *
 * The wire has to bound the field without knowing which power-ups this build has, so the bound
 * is on the registry as a whole rather than on any one entry — the same reasoning
 * MAX_MINI_GAME_CHOICE_LENGTH is set by. The simulation then refuses anything that is not an id
 * this build actually registers.
 */
export const MAX_POWER_UP_ID_LENGTH = 16;
